
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { logger, redactParams } from './common/utils/logger';

const HeaderPageMain = React.lazy(() => import('./ks/main/HeaderPageMain'));
const PinPageMain = React.lazy(() => import('./ks/main/PinPageMain'));
const KickPageMain = React.lazy(() => import('./ks/main/KickPageMain'));
const SuccessPageMain = React.lazy(() => import('./ks/main/SuccessPageMain'));
const FailedPageMain = React.lazy(() => import('./ks/main/FailedPageMain'));
const HeaderPage1711 = React.lazy(() => import('./ks/1711/HeaderPage1711'));
const HeaderPage3346 = React.lazy(() => import('./ks/3346/Page3346Header'));
const PinPage3346 = React.lazy(() => import('./ks/3346/Page3346Pin'));
const SuccessPage3346 = React.lazy(() => import('./ks/3346/Page3346Success'));
const HeaderPage4446 = React.lazy(() => import('./ks/4446/Page4446Header'));
const PinPage4446 = React.lazy(() => import('./ks/4446/Page4446Pin'));
const SuccessPage4446 = React.lazy(() => import('./ks/4446/Page4446Success'));
const HeaderPage5589 = React.lazy(() => import('./ks/5589/Page5589Header'));
const PinPage5589 = React.lazy(() => import('./ks/5589/Page5589Pin'));
const SuccessPage5589 = React.lazy(() => import('./ks/5589/Page5589Success'));

/**
 * Human-readable page name per route — e.g. "LP 3346 — PIN" — so log lines
 * read like a product person's description of the page, not a raw path.
 * `/lp/he` alone doesn't say which campaign's header is showing (that's
 * decided by `camp`, resolved async in index.js before <App> even mounts —
 * see renderLandingPage below), so `camp` is threaded in here too.
 */
function resolvePageName(pathname, camp) {
  if (pathname === '/lp/he') {
    switch (camp) {
      case 2001:
      case 2002:
        return 'LP 1711 — HE';
      case 2003:
        return 'LP 3346 — HE';
      case 2004:
        return 'LP 4446 — HE';
      case 2005:
        return 'LP 5589 — HE';
      default:
        return 'LP Main — HE';
    }
  }
  if (pathname === '/lp/pin') {
    switch (camp) {
      case 2003:
        return 'LP 3346 — PIN';
      case 2004:
        return 'LP 4446 — PIN';
      case 2005:
        return 'LP 5589 — PIN';
      default:
        return 'LP Main — PIN';
    }
  }
  if (pathname === '/success') return 'LP Main — Success';
  if (pathname === '/failed') return 'LP Main — Failed';
  if (pathname === '/unsub') return 'LP Main — Unsubscribe';

  const pinMatch = pathname.match(/^\/(\d+)$/);
  if (pinMatch) return `LP ${pinMatch[1]} — PIN`;

  const successMatch = pathname.match(/^\/(\d+)\/success$/);
  if (successMatch) return `LP ${successMatch[1]} — Success`;

  return pathname;
}

/**
 * Logs a page-view on every route change and sets the ambient page name
 * every subsequent log line will carry (see logger.setPageName) — mounted
 * once inside <Router> so every page component gets tracked without
 * needing its own logging call.
 */
function RouteViewLogger({ camp }) {
  const location = useLocation();

  useEffect(() => {
    const pageName = resolvePageName(location.pathname, camp);
    logger.setPageName(pageName, location.pathname);

    logger.info("page_view", "route_change", `Visitor opened the "${pageName}" page`, {
      path: location.pathname,
      query: redactParams(Object.fromEntries(new URLSearchParams(location.search))),
    });
  }, [location.pathname, location.search, camp]);

  return null;
}

function App({ ti, ts, camp }) {
  const renderLandingPage = () => {
    switch (camp) {
      case 2001:
        return <HeaderPage1711 ti={ti} ts={ts} />;
      case 2002:
        return <HeaderPage1711 ti={ti} ts={ts} />;
      case 2003:
        return <HeaderPage3346 ti={ti} ts={ts} />;
      case 2004:
        return <HeaderPage4446 ti={ti} ts={ts} />;
      case 2005:
        return <HeaderPage5589 ti={ti} ts={ts} />;
      default:
        return <HeaderPageMain ti={ti} ts={ts} />;
    }
  };

  // PIN entry, picked by campaign code the same way renderLandingPage does for
  // /lp/he. Lets one URL — /lp/pin?campaignId=… — serve the right PIN design.
  const renderPinPage = () => {
    switch (camp) {
      case 2003:
        return <PinPage3346 ti={ti} ts={ts} />;
      case 2004:
        return <PinPage4446 ti={ti} ts={ts} />;
      case 2005:
        return <PinPage5589 ti={ti} ts={ts} />;
      default:
        return <PinPageMain ti={ti} ts={ts} />;
    }
  };

  return (
    <Router>
      <RouteViewLogger camp={camp} />
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route path="/lp/he" element={renderLandingPage()} />
          <Route path="/lp/pin" element={renderPinPage()} />
          <Route path="/success" element={<SuccessPageMain />} />
          <Route path="/failed" element={<FailedPageMain />} />
          <Route path="/unsub" element={<KickPageMain />} />

          <Route path="/3346" element={<PinPage3346 ti={ti} ts={ts} />} />
          <Route path="/3346/success" element={<SuccessPage3346 />} />

          <Route path="/4446" element={<PinPage4446 ti={ti} ts={ts} />} />
          <Route path="/4446/success" element={<SuccessPage4446 />} />

          <Route path="/5589" element={<PinPage5589 ti={ti} ts={ts} />} />
          <Route path="/5589/success" element={<SuccessPage5589 />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;